import { Text } from "@mantine/core";
import {
  Dropzone,
  DropzoneProps,
  FileWithPath,
  MIME_TYPES,
} from "@mantine/dropzone";
import { useOpenCv } from "opencv-react";
import { ReactNode, useRef } from "react";
import { useTranslation } from "react-i18next";

const QUALITY = 0.9;
const MAX_DIMENSION = 2000;

interface ImageUploaderProps
  extends Omit<
    DropzoneProps,
    "children" | "openRef" | "accept" | "styles" | "onDrop"
  > {
  children?: ReactNode;
  compressImage?: boolean;
  grayScale?: boolean;
  error?: boolean;
  onImageUpload: (image: Blob | null) => void;
  setIsUploading: (isUploading: boolean | (() => boolean)) => void;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  onImageUpload,
  setIsUploading,
  children,
  compressImage = true,
  grayScale = true,
  error = false,
  ...dropzoneProps
}) => {
  const { t } = useTranslation("common");
  const openRef = useRef<() => void>(null);
  const { cv, loaded } = useOpenCv();

  const handleChangeImage = async (files: FileWithPath[]) => {
    if (!files.length || !loaded) {
      return;
    }

    const promiseCompressImages = files.map(async file => {
      if (file.type === "application/pdf") {
        return onImageUpload(file);
      }

      const canvas = document.createElement("canvas");

      const image = new Image();

      setIsUploading(() => true);

      image.onload = () => {
        const srcMat = cv.imread(image);

        const grayMat = new cv.Mat();

        const resizedImage = new cv.Mat();

        let newWidth, newHeight;

        if (srcMat.cols > MAX_DIMENSION || srcMat.rows > MAX_DIMENSION) {
          const aspectRatio = srcMat.cols / srcMat.rows;
          newWidth =
            aspectRatio > 1
              ? MAX_DIMENSION
              : (newHeight = MAX_DIMENSION) * aspectRatio;

          newHeight =
            aspectRatio > 1 ? MAX_DIMENSION / aspectRatio : MAX_DIMENSION;
        } else {
          // Image fits within the limits
          newWidth = srcMat.cols;
          newHeight = srcMat.rows;
        }

        const size = new cv.Size(newWidth, newHeight);

        cv.cvtColor(srcMat, grayMat, cv.COLOR_BGR2GRAY);

        cv.resize(grayScale ? grayMat : srcMat, resizedImage, size);

        cv.imshow(canvas, resizedImage);

        srcMat.delete();
        grayMat.delete();
        resizedImage.delete();

        canvas.toBlob(
          blob => {
            onImageUpload(blob);
            setIsUploading(() => false);
          },
          file.type,
          QUALITY
        );
      };

      image.src = URL.createObjectURL(file);
    });

    Promise.all(promiseCompressImages);
  };

  return (
    <Dropzone
      {...dropzoneProps}
      styles={{
        inner: {
          pointerEvents: "all",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        root: {
          border: error
            ? "2px dashed var(--color-red-alert)"
            : "1px dashed var(--mantine-color-gray-4)",
        },
      }}
      accept={[
        MIME_TYPES.webp,
        MIME_TYPES.avif,
        MIME_TYPES.jpeg,
        MIME_TYPES.png,
        MIME_TYPES.pdf,
      ]}
      useFsAccessApi={false}
      onDrop={handleChangeImage}
      activateOnClick={false}
      openRef={openRef}
      onClick={() => openRef.current?.()}
      className="cursor-pointer"
    >
      {children ? children : <Text ta="center">{t("dragAndDrop")}</Text>}
    </Dropzone>
  );
};

export default ImageUploader;
